import * as React from 'react';
import {
  Checkbox,
  OutlinedInput,
  MenuItem,
  FormControl,
  ListItemText,
  Select,
  ListSubheader,
  type TextFieldProps,
} from '@mui/material';
import { type DropDownSelectOption } from '../DropdownSelect/DropdownSelect';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

export type GroupedSelectOption = {
  category: string;
  categoryOption: DropDownSelectOption[];
};

type GroupedSelectProps = Omit<TextFieldProps, 'onChange'> & {
  options: GroupedSelectOption[];
  onChange: (selectedValue: string[]) => void;
  value: string[];
  displayLabel?: boolean;
};

function replaceValuesWithLabels(
  initialVal: string[],
  options: GroupedSelectOption[],
): string[] {
  return initialVal.map(value => {
    // Iterate over each category to find the matching option
    for (const category of options) {
      const option = category.categoryOption.find(opt => opt.value === value);
      if (option) {
        return option.label; // Return the label if found
      }
    }
    return value; // Return the original value if no match is found
  });
}
export const GroupedSelect: React.FunctionComponent<GroupedSelectProps> = (
  props: GroupedSelectProps,
) => {
  const { options, onChange, displayLabel = true, value } = props;

  const [selectOptionValues, setSelectOptionValues] =
    React.useState<string[]>(value);

  const handleSelect = (value: string, checked: boolean): void => {
    const preSelectOptions = [...selectOptionValues];
    const curSelectOptions = checked
      ? [...preSelectOptions, value]
      : preSelectOptions.filter(opt => opt !== value);
    setSelectOptionValues(curSelectOptions);
    onChange(curSelectOptions);
  };

  return (
    <div>
      <FormControl sx={{ m: 1, width: '90%', maxWidth: '340px' }}>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          size="small"
          multiple
          displayEmpty
          value={selectOptionValues}
          input={<OutlinedInput />}
          renderValue={selected => {
            if (selected.length === 0) {
              return <em>[Default Select All]</em>;
            }
            // to avoid display enums directly
            return (
              displayLabel
                ? replaceValuesWithLabels(selected, options)
                : selected
            ).join(',');
          }}
          inputProps={{ 'aria-label': 'Without label' }}
          MenuProps={MenuProps}
        >
          <MenuItem disabled value="">
            <em>[Default Select All]</em>
          </MenuItem>

          {options.map((cate, index) => (
            <div key={index}>
              <ListSubheader>{cate.category}</ListSubheader>
              {cate.categoryOption.map(option => {
                const checked = selectOptionValues.includes(option.value);
                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    onClick={() => {
                      handleSelect(option.value, !checked);
                    }}
                  >
                    <Checkbox size="small" checked={checked} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                );
              })}
            </div>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
